import { useEffect, useState } from "react";
import { TriangleAlert } from "lucide-react";
import { api, money } from "@/lib/api";
import { Kpi, PageHead, EmptyState } from "@/components/Bits";
import { ExportButtons } from "@/components/ExportButtons";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const Rollup = ({ title, rows, testid, showQty = true }) => (
  <Card className="border-brand-line shadow-none">
    <CardHeader>
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent className="overflow-x-auto p-0">
      <Table data-testid={testid} className="mobile-cards">
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            {showQty && <TableHead className="text-right">Quantity</TableHead>}
            <TableHead className="text-right">Entries</TableHead>
            <TableHead className="text-right">Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="py-8 text-center text-slate-500">
                Nothing logged this month.
              </TableCell>
            </TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.name} data-testid={`${testid}-row-${r.name}`}>
              <TableCell data-label="Name" className="font-medium">
                {r.name}
              </TableCell>
              {showQty && (
                <TableCell data-label="Quantity" className="text-right">
                  {Number(r.quantity).toFixed(3)} {r.unit}
                </TableCell>
              )}
              <TableCell data-label="Entries" className="text-right text-slate-500">
                {r.entries}
              </TableCell>
              <TableCell data-label="Value" className="text-right font-semibold">
                {money(r.value)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

export default function Wastage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/wastage").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const t = data.totals;

  return (
    <div>
      <PageHead
        title="Wastage"
        subtitle={`Material wasted this month · ${data.month} · valued at HQ cost price`}
      >
        <ExportButtons
          testidPrefix="wastage"
          filename="veloskin-wastage"
          title="Wastage report"
          subtitle={data.month}
          columns={[
            { label: "Material", value: (r) => r.name },
            { label: "Quantity", value: (r) => r.quantity },
            { label: "Unit", value: (r) => r.unit },
            { label: "Entries", value: (r) => r.entries },
            { label: "Value", value: (r) => r.value },
          ]}
          rows={data.by_material}
        />
      </PageHead>

      <div className="mb-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Wastage value" value={money(t.value)} testid="kpi-wastage-value" accent="text-crimson-600" />
        <Kpi label="Entries logged" value={t.entries} testid="kpi-wastage-entries" />
        <Kpi label="Materials affected" value={t.materials} testid="kpi-wastage-materials" />
        <Kpi
          label="High-wastage jobs"
          value={t.high_wastage_jobs}
          testid="kpi-high-wastage-jobs"
          accent="text-amber-600"
        />
      </div>

      <Card className="mb-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Per studio</CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Total value wasted this month, the most-wasted material and the most frequent reason.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="wastage-studio-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                <TableHead>Studio</TableHead>
                <TableHead className="text-right">Wastage value</TableHead>
                <TableHead className="text-right">Entries</TableHead>
                <TableHead>Top wasted material</TableHead>
                <TableHead>Most frequent reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.by_studio.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="p-0">
                    <EmptyState
                      icon={TriangleAlert}
                      testid="wastage-empty-state"
                      title="No wastage logged this month"
                      hint="Wastage is logged from a job's detail page — film offcuts, spills and rework all get tracked against the responsible party."
                    />
                  </TableCell>
                </TableRow>
              )}
              {data.by_studio.map((r) => (
                <TableRow key={r.studio_id} data-testid={`wastage-studio-${r.studio_id}`}>
                  <TableCell data-label="Studio" className="font-medium">
                    {r.studio_name}
                  </TableCell>
                  <TableCell data-label="Wastage value" className="text-right font-semibold text-crimson-600">
                    {money(r.value)}
                  </TableCell>
                  <TableCell data-label="Entries" className="text-right text-slate-500">
                    {r.entries}
                  </TableCell>
                  <TableCell data-label="Top wasted material">
                    {r.top_material}
                    <span className="block text-xs text-slate-500">
                      {money(r.top_material_value)}
                    </span>
                  </TableCell>
                  <TableCell data-label="Most frequent reason">{r.top_reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Rollup title="By material" rows={data.by_material} testid="wastage-material-table" />
        <Rollup title="By reason" rows={data.by_reason} testid="wastage-reason-table" showQty={false} />
        <Rollup title="By responsible party" rows={data.by_party} testid="wastage-party-table" showQty={false} />
        <Rollup title="By installer" rows={data.by_installer} testid="wastage-installer-table" />
      </div>

      {data.high_wastage_jobs.length > 0 && (
        <Card className="mt-6 border-amber-200 shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">High-wastage jobs for review</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              A single job wasted more than 20% of the standard consumption for a material.
            </p>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="high-wastage-list">
            {data.high_wastage_jobs.map((j) => (
              <div
                key={j.job_id}
                className="rounded-md border border-amber-200 bg-amber-50/50 p-3 text-sm"
                data-testid={`high-wastage-${j.job_id}`}
              >
                {j.flags.map((f) => (
                  <p key={f} className="flex items-start gap-1.5 text-amber-800">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" /> {f}
                  </p>
                ))}
                <a
                  href={`/jobs/${j.job_id}`}
                  className="mt-1 inline-block text-xs font-semibold underline"
                >
                  Open job
                </a>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
